import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatUSD } from "../../lib/api";
import Header from "../../components/Header";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../components/ui/dialog";
import {
  CaretLeft, Bank, CheckCircle, Warning, MagnifyingGlass, FileText, ArrowSquareOut, Receipt, Envelope,
  DownloadSimple, FileXls,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { openPayoutReceipt } from "./agents/payoutReceiptReport";

const TYPE_LABEL = {
  issuance: "Card issuance",
  topup: "Top-up",
  cash_deposit: "Cash deposit",
};

function StatusPill({ status }) {
  if (status === "paid") return (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-200 text-emerald-800 text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5" data-testid="payout-status-paid">
      <CheckCircle size={11} weight="fill" /> Paid
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-200 text-amber-800 text-[10px] tracking-widest uppercase font-semibold px-2 py-0.5" data-testid="payout-status-unpaid">
      Unpaid
    </span>
  );
}

export default function AgentPayoutsAdmin() {
  const [tab, setTab] = useState("ledger"); // "ledger" | "history"
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ paid: { amount: 0, count: 0 }, unpaid: { amount: 0, count: 0 } });
  const [total, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("unpaid"); // "unpaid" | "paid" | "all"
  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paidRef, setPaidRef] = useState("");
  const [paidNote, setPaidNote] = useState("");
  const [busy, setBusy] = useState(false);
  // After a successful "Process payment", we hold the payout_ids that were
  // just created so the user can click straight through to the printable
  // receipts.
  const [successPayouts, setSuccessPayouts] = useState(null);
  // Payout-history tab data
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  // Per-row email-busy map: { [payout_id]: true } while a send is in flight.
  const [emailingIds, setEmailingIds] = useState(() => new Set());
  // CSV export busy + NACHA file selection state (history tab)
  const [exportingCsv, setExportingCsv] = useState(false);
  const [nachaSelectedIds, setNachaSelectedIds] = useState(() => new Set());
  const [nachaBusy, setNachaBusy] = useState(false);
  const [nachaResultOpen, setNachaResultOpen] = useState(false);
  const [nachaResult, setNachaResult] = useState(null);
  // Auto-email-after-processing preference, persisted to localStorage so
  // admins don't have to re-tick the box on every payout batch.
  const [autoEmail, setAutoEmail] = useState(() => {
    try { return localStorage.getItem("zcardon_payout_auto_email") === "1"; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("zcardon_payout_auto_email", autoEmail ? "1" : "0"); }
    catch { /* localStorage blocked — non-fatal */ }
  }, [autoEmail]);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("q", search.trim());
      params.set("limit", "200");
      const r = await api.get(`/admin/agent-payouts?${params.toString()}`);
      setRows(r.data?.rows || []);
      setTotals(r.data?.totals || { paid: { amount: 0, count: 0 }, unpaid: { amount: 0, count: 0 } });
      setTotalCount(r.data?.total ?? 0);
      // Drop selections that no longer match the visible page
      setSelectedIds(prev => {
        const visibleIds = new Set((r.data?.rows || []).map(x => x.id));
        return new Set([...prev].filter(id => visibleIds.has(id)));
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load payouts");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, search]);

  useEffect(() => { refresh(); }, [refresh]);

  // Only unpaid rows are actionable (we don't un-mark paid rows here).
  const actionable = useMemo(() => rows.filter(r => r.status === "unpaid"), [rows]);

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const toggleAll = () => {
    setSelectedIds(prev => {
      const all = actionable.length > 0 && actionable.every(r => prev.has(r.id));
      if (all) {
        const n = new Set(prev);
        actionable.forEach(r => n.delete(r.id));
        return n;
      }
      const n = new Set(prev);
      actionable.forEach(r => n.add(r.id));
      return n;
    });
  };

  // Group selected rows by agent so the confirmation dialog can show
  // "you're about to pay 3 agents totaling $XX" — and to highlight any
  // agents that don't have bank info yet.
  const selectedSummary = useMemo(() => {
    const byAgent = new Map();
    let totalAmt = 0;
    let missingBank = 0;
    rows.forEach(r => {
      if (!selectedIds.has(r.id)) return;
      const key = r.agent_id;
      const existing = byAgent.get(key) || {
        agent_id: r.agent_id, agent_full_name: r.agent_full_name,
        agent_code: r.agent_code_current, count: 0, amount: 0,
        bank_info_complete: r.bank_info_complete,
      };
      existing.count += 1;
      existing.amount += Number(r.commission_amount || 0);
      byAgent.set(key, existing);
      totalAmt += Number(r.commission_amount || 0);
      if (!r.bank_info_complete) missingBank += 1;
    });
    return { agents: [...byAgent.values()], totalAmt, missingBank, count: selectedIds.size };
  }, [rows, selectedIds]);

  const confirmMarkPaid = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/agent-payouts/mark-paid", {
        commission_ids: [...selectedIds],
        paid_ref: paidRef.trim(),
        paid_note: paidNote.trim(),
      });
      toast.success(`Processed payment for ${r.data.agents_touched} agent${r.data.agents_touched === 1 ? "" : "s"} — ${r.data.marked_paid} commission${r.data.marked_paid === 1 ? "" : "s"} settled.`);
      setConfirmOpen(false);
      setPaidRef("");
      setPaidNote("");
      setSelectedIds(new Set());

      const payoutIds = r.data.payout_ids || [];
      if (payoutIds.length > 0) {
        setSuccessPayouts({
          ids: payoutIds,
          marked_paid: r.data.marked_paid,
          agents_touched: r.data.agents_touched,
          auto_email_status: autoEmail ? "pending" : "off",
          auto_emailed_to: [],
          auto_email_failed: [],
        });
        // Auto-email: fire all sends in parallel; report results back to the
        // success modal so the admin sees ✓ N receipts emailed automatically.
        if (autoEmail) {
          const results = await Promise.allSettled(
            payoutIds.map(pid => api.post(`/admin/payout-transactions/${pid}/email`)),
          );
          const ok = [];
          const failed = [];
          results.forEach((res, i) => {
            const pid = payoutIds[i];
            if (res.status === "fulfilled") {
              ok.push({ pid, to: res.value?.data?.emailed_to });
            } else {
              failed.push({ pid, error: res.reason?.response?.data?.detail || "send failed" });
            }
          });
          setSuccessPayouts(prev => prev ? {
            ...prev,
            auto_email_status: "done",
            auto_emailed_to: ok,
            auto_email_failed: failed,
          } : prev);
          if (ok.length > 0) toast.success(`Auto-emailed ${ok.length} receipt${ok.length === 1 ? "" : "s"} to agents.`);
          if (failed.length > 0) toast.error(`${failed.length} receipt email${failed.length === 1 ? "" : "s"} failed — try resending manually.`);
        }
      }
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not process payment");
    } finally {
      setBusy(false);
    }
  };

  const openReceipt = async (payoutId) => {
    try {
      const result = await openPayoutReceipt(api, payoutId);
      if (!result?.ok && result?.blocked) {
        toast.error("Popup blocked — allow pop-ups for this site to open the receipt.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load receipt");
    }
  };

  const emailReceipt = async (payoutId) => {
    setEmailingIds(prev => {
      const n = new Set(prev); n.add(payoutId); return n;
    });
    try {
      const r = await api.post(`/admin/payout-transactions/${payoutId}/email`);
      toast.success(`Receipt emailed to ${r.data?.emailed_to || "agent"}`);
      // Patch the local history row so the "Emailed at" timestamp shows
      // without a full refresh.
      setHistory(prev => prev.map(h => h.id === payoutId ? {
        ...h,
        last_emailed_at: r.data?.emailed_at || new Date().toISOString(),
        last_emailed_to: r.data?.emailed_to,
        emailed_count: (h.emailed_count || 0) + 1,
      } : h));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not email receipt");
    } finally {
      setEmailingIds(prev => {
        const n = new Set(prev); n.delete(payoutId); return n;
      });
    }
  };

  // Click an agent name → preselect ALL that agent's unpaid commissions
  // currently in view and open the process-payment confirm dialog. The
  // common case of "pay this agent everything they're owed" becomes 1
  // click instead of N checkbox clicks.
  const payAgent = (agentId, agentName) => {
    const agentUnpaid = rows.filter(r => r.agent_id === agentId && r.status === "unpaid");
    if (agentUnpaid.length === 0) {
      toast.info(`No unpaid commissions for ${agentName} in the current view.`);
      return;
    }
    setSelectedIds(new Set(agentUnpaid.map(r => r.id)));
    // Defer dialog opening to next tick so the bulk-bar can render the
    // selection summary first — gives the admin a confidence cue.
    setTimeout(() => setConfirmOpen(true), 50);
  };

  // Click a receiver name/email → narrow the ledger to that receiver
  // across ALL agents + ALL statuses. Useful when investigating
  // disputes, refunds, or duplicate issuances for a single end-user.
  const filterByReceiver = (receiverEmail, receiverName) => {
    const q = (receiverEmail || receiverName || "").trim();
    if (!q) {
      toast.info("Receiver has no email or name to filter by.");
      return;
    }
    setSearch(q);
    // Also broaden the status filter so the admin sees both paid + unpaid
    // history for this receiver in one view.
    setStatusFilter("all");
    // Clear any current selection — different scope, different intent.
    setSelectedIds(new Set());
    toast.info(`Filtered to receiver: ${receiverName || receiverEmail}. Clear search to reset.`);
  };

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const r = await api.get("/admin/payout-transactions?limit=200");
      setHistory(r.data?.rows || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load payout history");
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => { if (tab === "history") loadHistory(); }, [tab, loadHistory]);

  // ─── CSV export ────────────────────────────────────────────────────
  const exportCsv = async () => {
    setExportingCsv(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (search.trim()) params.set("q", search.trim());
      const r = await api.get(`/admin/agent-payouts/export.csv?${params.toString()}`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      // Try to read filename from Content-Disposition; fall back to a sane default.
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      a.download = m ? m[1] : `zcardon-agent-payouts.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("CSV downloaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV export failed");
    } finally {
      setExportingCsv(false);
    }
  };

  // ─── NACHA selection helpers (history tab) ─────────────────────────
  const nachaToggleOne = (payoutId) => {
    setNachaSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(payoutId)) n.delete(payoutId);
      else n.add(payoutId);
      return n;
    });
  };
  const nachaToggleAll = () => {
    if (nachaSelectedIds.size === history.length) {
      setNachaSelectedIds(new Set());
    } else {
      setNachaSelectedIds(new Set(history.map(h => h.id)));
    }
  };
  const nachaSelectedTotal = useMemo(
    () => history
      .filter(h => nachaSelectedIds.has(h.id))
      .reduce((sum, h) => sum + Number(h.total_amount || 0), 0),
    [history, nachaSelectedIds],
  );

  const generateNacha = async () => {
    if (nachaSelectedIds.size === 0) {
      toast.error("Select at least one processed payout first.");
      return;
    }
    setNachaBusy(true);
    try {
      const r = await api.post(
        "/admin/agent-payouts/nacha-batch",
        { payout_ids: Array.from(nachaSelectedIds) },
        { responseType: "blob" },
      );
      // Pull headers BEFORE Blob consumption.
      const entryCount = r.headers?.["x-nacha-entry-count"] || "?";
      const totalCents = Number(r.headers?.["x-nacha-total-cents"] || 0);
      const skipped = Number(r.headers?.["x-nacha-skipped-count"] || 0);
      const fileId = r.headers?.["x-nacha-file-id"] || "";
      const cd = r.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `zcardon-nacha.ach`;
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/plain" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setNachaResult({
        filename,
        entryCount,
        totalUsd: totalCents / 100,
        skipped,
        fileId,
      });
      setNachaResultOpen(true);
      setNachaSelectedIds(new Set());
    } catch (err) {
      // Errors come back as JSON-encoded blob; parse before showing.
      let detail = "NACHA generation failed";
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        try {
          const text = await blob.text();
          const parsed = JSON.parse(text);
          detail = parsed.detail || detail;
        } catch { /* keep default */ }
      } else if (err?.response?.data?.detail) {
        detail = err.response.data.detail;
      }
      toast.error(detail);
    } finally {
      setNachaBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <Header />
      <div className="max-w-[1500px] mx-auto px-6 py-10">
        <Link to="/admin" className="text-xs uppercase tracking-widest text-[#4A5C50] hover:text-[#184A30] inline-flex items-center gap-1" data-testid="back-to-admin">
          <CaretLeft size={12} /> Admin overview
        </Link>
        <h1 className="serif text-5xl mt-2" data-testid="agent-payouts-heading">Agent commission payouts</h1>
        <p className="text-[#4A5C50] mt-2 max-w-2xl text-sm">
          Every commission earned by every agent. Filter by status, process
          payouts in bulk, generate printable receipts, and see exactly
          which receiver each commission was earned on alongside the
          agent&apos;s bank info for payout routing.
          <span className="block mt-1.5 text-xs text-[#184A30]">
            <strong>Shortcuts:</strong> click an <strong>agent name</strong> to instantly pay all their unpaid commissions · click a <strong>receiver name</strong> to filter the ledger to that receiver across all agents and statuses.
          </span>
        </p>

        <div className="mt-6 inline-flex items-center gap-1 bg-white border border-[#E5E0D8] rounded-full p-1" data-testid="payouts-tabs">
          {[
            { id: "ledger", label: "Ledger" },
            { id: "history", label: "Payout history" },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`text-xs uppercase tracking-widest px-4 py-2 rounded-full transition ${tab === t.id ? "bg-[#184A30] text-white" : "text-[#4A5C50] hover:bg-stone-50"}`}
              data-testid={`payouts-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "ledger" ? (
        <>
        <div className="grid md:grid-cols-3 gap-4 mt-8">
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-4" data-testid="tile-total-unpaid">
            <p className="text-[10px] tracking-[0.3em] uppercase text-amber-700">Owed (unpaid)</p>
            <p className="serif text-3xl mt-1 text-[#112318]">{formatUSD(totals.unpaid.amount)}</p>
            <p className="text-xs text-[#4A5C50] mt-1">{totals.unpaid.count} row{totals.unpaid.count === 1 ? "" : "s"}</p>
          </div>
          <div className="bg-white border border-[#E5E0D8] rounded-2xl p-4" data-testid="tile-total-paid">
            <p className="text-[10px] tracking-[0.3em] uppercase text-emerald-700">Paid out</p>
            <p className="serif text-3xl mt-1 text-[#112318]">{formatUSD(totals.paid.amount)}</p>
            <p className="text-xs text-[#4A5C50] mt-1">{totals.paid.count} row{totals.paid.count === 1 ? "" : "s"}</p>
          </div>
          <div className="bg-[#184A30] text-white rounded-2xl p-4" data-testid="tile-total-rows">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#8BA888]">Matching filter</p>
            <p className="serif text-3xl mt-1">{total}</p>
            <p className="text-xs text-[#8BA888] mt-1">commission rows</p>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap items-end gap-3 justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            {["unpaid", "paid", "all"].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${statusFilter === s ? "bg-[#184A30] border-[#184A30] text-white" : "bg-white border-[#E5E0D8] text-[#4A5C50] hover:bg-stone-50"}`}
                data-testid={`filter-status-${s}`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto sm:ml-auto">
            <div className="relative flex-1 sm:w-96">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4A5C50]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search receiver name/email, agent name/code…"
                className="w-full pl-9 pr-3 py-2 rounded-full border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:border-[#184A30] focus:ring-1 focus:ring-[#184A30]"
                data-testid="payouts-search-input"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={exportCsv}
              disabled={exportingCsv}
              className="shrink-0 whitespace-nowrap"
              data-testid="payouts-export-csv-btn"
              title="Download every commission row in the current filter as CSV"
            >
              <FileXls size={14} className="mr-1" />
              {exportingCsv ? "Exporting…" : "Export CSV"}
            </Button>
          </div>
        </div>

        {/* Sticky bulk action bar — only when ≥1 unpaid row selected. */}
        {selectedIds.size > 0 && (
          <div
            className="sticky top-[80px] z-30 mt-4 bg-[#184A30] text-white rounded-2xl px-5 py-3 flex items-center justify-between gap-3 shadow-lg shadow-[#184A30]/20"
            data-testid="payouts-bulk-bar"
          >
            <div className="text-sm">
              <span className="font-semibold" data-testid="payouts-selection-count">{selectedIds.size}</span> commission
              {selectedIds.size === 1 ? "" : "s"} selected · {formatUSD(selectedSummary.totalAmt)} across {selectedSummary.agents.length} agent{selectedSummary.agents.length === 1 ? "" : "s"}
              {selectedSummary.missingBank > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-amber-200">
                  <Warning size={12} weight="fill" /> {selectedSummary.missingBank} row{selectedSummary.missingBank === 1 ? " is" : "s are"} on agents missing bank info
                </span>
              )}
              <button onClick={() => setSelectedIds(new Set())} className="ml-3 text-xs text-white/70 hover:text-white underline underline-offset-2" data-testid="payouts-clear-selection">Clear</button>
            </div>
            <Button
              size="sm"
              onClick={() => setConfirmOpen(true)}
              disabled={busy}
              className="bg-emerald-500 hover:bg-emerald-600 text-white border-0"
              data-testid="payouts-mark-paid-btn"
            >
              <Receipt size={14} className="mr-1" /> Process payment for {selectedIds.size}
            </Button>
          </div>
        )}

        {loading ? (
          <div className="mt-10 text-center text-[#4A5C50] text-sm">Loading payouts…</div>
        ) : rows.length === 0 ? (
          <div className="mt-10 text-center text-[#4A5C50] text-sm bg-white border border-[#E5E0D8] rounded-2xl py-10" data-testid="payouts-empty">
            No commission rows match the current filter.
          </div>
        ) : (
          <div className="mt-4 bg-white border border-[#E5E0D8] rounded-2xl overflow-x-auto">
            <table className="w-full text-sm min-w-[1300px]" data-testid="payouts-table">
              <thead className="text-left text-[#4A5C50] text-[10px] uppercase tracking-widest border-b border-[#E5E0D8]">
                <tr>
                  <th className="p-3 w-10">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-[#4A5C50] cursor-pointer accent-[#184A30]"
                      checked={actionable.length > 0 && actionable.every(r => selectedIds.has(r.id))}
                      ref={(el) => {
                        if (el) {
                          const some = actionable.some(r => selectedIds.has(r.id));
                          const all = actionable.length > 0 && actionable.every(r => selectedIds.has(r.id));
                          el.indeterminate = some && !all;
                        }
                      }}
                      onChange={toggleAll}
                      data-testid="payouts-select-all"
                      disabled={actionable.length === 0}
                    />
                  </th>
                  <th className="p-3">Date earned</th>
                  <th className="p-3">Paid at</th>
                  <th className="p-3">Agent</th>
                  <th className="p-3">Agent bank</th>
                  <th className="p-3">Receiver</th>
                  <th className="p-3">Type</th>
                  <th className="p-3 text-right">Tx amount</th>
                  <th className="p-3 text-right">Commission</th>
                  <th className="p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(r => {
                  const checkable = r.status === "unpaid";
                  const checked = selectedIds.has(r.id);
                  return (
                    <tr
                      key={r.id}
                      className={`border-t border-[#E5E0D8] hover:bg-stone-50 ${checked ? "bg-[#CBA35C]/10" : ""}`}
                      data-testid={`payout-row-${r.id}`}
                    >
                      <td className="p-3">
                        {checkable ? (
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-[#4A5C50] cursor-pointer accent-[#184A30]"
                            checked={checked}
                            onChange={() => toggleOne(r.id)}
                            data-testid={`payout-checkbox-${r.id}`}
                          />
                        ) : (
                          <span className="block w-4 h-4 opacity-30 rounded border border-[#E5E0D8]" />
                        )}
                      </td>
                      <td className="p-3 text-xs text-[#4A5C50]">{r.earned_at ? new Date(r.earned_at).toLocaleString() : "—"}</td>
                      <td className="p-3 text-xs text-[#4A5C50]">{r.paid_at ? new Date(r.paid_at).toLocaleString() : "—"}</td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={() => payAgent(r.agent_id, r.agent_full_name)}
                          className="text-left group focus:outline-none"
                          data-testid={`pay-agent-${r.agent_id}`}
                          title={`Pay all unpaid commissions for ${r.agent_full_name}`}
                        >
                          <div className="font-semibold text-[#112318] group-hover:text-[#184A30] group-hover:underline underline-offset-2 transition">
                            {r.agent_full_name || "—"}
                          </div>
                          <div className="text-[10px] mono text-[#4A5C50]">{r.agent_code_current}</div>
                        </button>
                      </td>
                      <td className="p-3 text-xs">
                        {r.bank_info_complete ? (
                          <>
                            <div className="flex items-center gap-1 text-[#112318]">
                              <Bank size={11} weight="duotone" /> {r.bank_name}
                            </div>
                            <div className="mono text-[10px] text-[#4A5C50] mt-0.5">
                              {r.bank_account_holder} · •••• {r.bank_account_last4}
                            </div>
                            <div className="text-[10px] text-[#4A5C50] mt-0.5">
                              Routing •••• {r.bank_routing_last4}
                              {r.swift_code && <> · SWIFT <span className="mono">{r.swift_code}</span></>}
                            </div>
                            {r.iban_last4 && (
                              <div className="text-[10px] text-[#4A5C50] mt-0.5">
                                IBAN <span className="mono">{r.iban_country} •••• {r.iban_last4}</span>
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded bg-amber-100 border border-amber-200 text-amber-800 text-[10px] uppercase tracking-widest px-1.5 py-0.5 font-semibold" data-testid={`payouts-no-bank-${r.id}`}>
                            <Warning size={10} weight="fill" /> No bank info
                          </span>
                        )}
                      </td>
                      <td className="p-3 text-xs">
                        <button
                          type="button"
                          onClick={() => filterByReceiver(r.receiver_email || r.customer_email, r.receiver_name)}
                          className="text-left group focus:outline-none w-full"
                          data-testid={`filter-receiver-${r.id}`}
                          title={`Filter ledger to commissions for ${r.receiver_name || r.receiver_email || "this receiver"}`}
                        >
                          <div className="font-semibold text-[#112318] group-hover:text-[#184A30] group-hover:underline underline-offset-2 transition">
                            {r.receiver_name || "—"}
                          </div>
                          <div className="text-[10px] text-[#4A5C50]">{r.receiver_email || r.customer_email || ""}</div>
                          <div className="text-[10px] text-[#4A5C50]">{r.receiver_phone || ""}</div>
                        </button>
                      </td>
                      <td className="p-3 text-xs text-[#4A5C50]">{TYPE_LABEL[r.transaction_type] || r.transaction_type}</td>
                      <td className="p-3 text-right mono text-[#4A5C50]">{formatUSD(r.transaction_amount || 0)}</td>
                      <td className="p-3 text-right mono font-semibold text-[#112318]">{formatUSD(r.commission_amount || 0)}</td>
                      <td className="p-3"><StatusPill status={r.status} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </>
        ) : (
          <div className="mt-8">
            <div className="flex items-start justify-between flex-wrap gap-3">
              <div>
                <h2 className="serif text-3xl text-[#112318]">Payout history</h2>
                <p className="text-[#4A5C50] text-sm mt-1">Every formal commission payout processed. Click any row to re-open its printable receipt, or select rows below to generate a NACHA ACH file for one-shot bank upload.</p>
              </div>
              {history.length > 0 && (
                <Button
                  size="sm"
                  onClick={generateNacha}
                  disabled={nachaBusy || nachaSelectedIds.size === 0}
                  className="bg-[#184A30] hover:bg-[#0F3320] text-white whitespace-nowrap"
                  data-testid="payouts-nacha-btn"
                  title="Generate a NACHA ACH credit file for the selected payouts"
                >
                  <DownloadSimple size={14} className="mr-1" />
                  {nachaBusy
                    ? "Generating…"
                    : nachaSelectedIds.size > 0
                      ? `Generate NACHA · ${nachaSelectedIds.size} payout${nachaSelectedIds.size === 1 ? "" : "s"} · ${formatUSD(nachaSelectedTotal)}`
                      : "Generate NACHA file"}
                </Button>
              )}
            </div>
            {historyLoading ? (
              <div className="mt-6 text-center text-[#4A5C50] text-sm">Loading…</div>
            ) : history.length === 0 ? (
              <div className="mt-6 text-center text-[#4A5C50] text-sm bg-white border border-[#E5E0D8] rounded-2xl py-10" data-testid="payouts-history-empty">
                No payouts processed yet. Use the Ledger tab to process your first one.
              </div>
            ) : (
              <div className="mt-6 bg-white border border-[#E5E0D8] rounded-2xl overflow-x-auto">
                <table className="w-full text-sm min-w-[1000px]" data-testid="payouts-history-table">
                  <thead className="text-left text-[#4A5C50] text-[10px] uppercase tracking-widest border-b border-[#E5E0D8]">
                    <tr>
                      <th className="p-3 w-10">
                        <input
                          type="checkbox"
                          className="h-4 w-4 rounded border-[#4A5C50] cursor-pointer accent-[#184A30]"
                          checked={history.length > 0 && nachaSelectedIds.size === history.length}
                          ref={(el) => {
                            if (el) {
                              const some = nachaSelectedIds.size > 0;
                              const all = nachaSelectedIds.size === history.length;
                              el.indeterminate = some && !all;
                            }
                          }}
                          onChange={nachaToggleAll}
                          data-testid="payouts-history-select-all"
                        />
                      </th>
                      <th className="p-3">Processed at</th>
                      <th className="p-3">Receipt #</th>
                      <th className="p-3">Agent</th>
                      <th className="p-3">Rows</th>
                      <th className="p-3 text-right">Amount</th>
                      <th className="p-3">Reference</th>
                      <th className="p-3">Processed by</th>
                      <th className="p-3">Email status</th>
                      <th className="p-3 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {history.map(h => (
                      <tr key={h.id} className={`border-t border-[#E5E0D8] hover:bg-stone-50 ${nachaSelectedIds.has(h.id) ? "bg-[#184A30]/5" : ""}`} data-testid={`payouts-history-row-${h.id}`}>
                        <td className="p-3">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-[#4A5C50] cursor-pointer accent-[#184A30]"
                            checked={nachaSelectedIds.has(h.id)}
                            onChange={() => nachaToggleOne(h.id)}
                            data-testid={`payouts-history-checkbox-${h.id}`}
                          />
                        </td>
                        <td className="p-3 text-xs text-[#4A5C50]">{new Date(h.processed_at).toLocaleString()}</td>
                        <td className="p-3 mono text-xs font-semibold text-[#184A30]">{h.id.slice(0, 8).toUpperCase()}</td>
                        <td className="p-3 text-xs">
                          <div className="font-semibold text-[#112318]">{h.agent_full_name}</div>
                          <div className="mono text-[10px] text-[#4A5C50]">{h.agent_code} · {h.agent_email}</div>
                        </td>
                        <td className="p-3 text-xs text-[#4A5C50]">{h.row_count}</td>
                        <td className="p-3 mono text-right font-semibold">{formatUSD(h.total_amount)}</td>
                        <td className="p-3 mono text-[11px] text-[#4A5C50]">{h.payment_ref || "—"}</td>
                        <td className="p-3 text-[11px] text-[#4A5C50]">{h.processed_by || "—"}</td>
                        <td className="p-3 text-[11px]" data-testid={`payouts-history-email-status-${h.id}`}>
                          {h.last_emailed_at ? (
                            <div className="text-emerald-700">
                              <div className="flex items-center gap-1 font-medium"><Envelope size={11} weight="fill" /> Sent</div>
                              <div className="text-[10px] text-[#4A5C50] mt-0.5">{new Date(h.last_emailed_at).toLocaleString()}</div>
                              {(h.emailed_count || 0) > 1 && <div className="text-[10px] text-[#4A5C50]">×{h.emailed_count}</div>}
                            </div>
                          ) : (
                            <span className="text-[#4A5C50]">Not sent</span>
                          )}
                        </td>
                        <td className="p-3 text-right whitespace-nowrap">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => emailReceipt(h.id)}
                            disabled={emailingIds.has(h.id) || !h.agent_email}
                            className="text-xs mr-1"
                            data-testid={`payouts-history-email-${h.id}`}
                            title={h.agent_email ? `Email receipt to ${h.agent_email}` : "Agent has no email on file"}
                          >
                            <Envelope size={12} className="mr-1" />
                            {emailingIds.has(h.id) ? "Sending…" : h.last_emailed_at ? "Re-send" : "Email"}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReceipt(h.id)}
                            className="text-xs"
                            data-testid={`payouts-history-receipt-${h.id}`}
                          >
                            <FileText size={12} className="mr-1" /> Open
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Success modal — shown right after a successful process payment */}
        <Dialog open={!!successPayouts} onOpenChange={(v) => { if (!v) setSuccessPayouts(null); }}>
          <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-lg" data-testid="payouts-success-dialog">
            <DialogHeader>
              <DialogTitle className="serif text-3xl text-emerald-700 flex items-center gap-2">
                <CheckCircle size={28} weight="fill" /> Payment processed
              </DialogTitle>
              <DialogDescription className="text-[#4A5C50]">
                {successPayouts?.marked_paid} commission row{successPayouts?.marked_paid === 1 ? "" : "s"} settled across {successPayouts?.agents_touched} agent{successPayouts?.agents_touched === 1 ? "" : "s"}. Click below to open each printable receipt.
              </DialogDescription>
            </DialogHeader>

            {successPayouts?.auto_email_status === "pending" && (
              <div className="rounded-lg bg-stone-100 border border-stone-200 text-[#4A5C50] text-xs p-3 flex items-center gap-2" data-testid="payouts-auto-email-pending">
                <span className="inline-block h-2 w-2 rounded-full bg-[#184A30] animate-pulse" />
                Auto-emailing receipts to {successPayouts?.ids?.length || 0} agent{successPayouts?.ids?.length === 1 ? "" : "s"}…
              </div>
            )}
            {successPayouts?.auto_email_status === "done" && (
              <div
                className={`rounded-lg border text-xs p-3 flex gap-2 ${
                  (successPayouts.auto_email_failed?.length || 0) === 0
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
                    : "bg-amber-50 border-amber-200 text-amber-900"
                }`}
                data-testid="payouts-auto-email-result"
              >
                <Envelope size={14} weight="fill" className="mt-0.5 shrink-0" />
                <div>
                  Auto-emailed <strong>{successPayouts.auto_emailed_to?.length || 0}</strong> receipt{(successPayouts.auto_emailed_to?.length || 0) === 1 ? "" : "s"}
                  {(successPayouts.auto_email_failed?.length || 0) > 0 && (
                    <> · <strong>{successPayouts.auto_email_failed.length}</strong> failed — use the per-row Email button to retry</>
                  )}
                </div>
              </div>
            )}
            <div className="space-y-2" data-testid="payouts-success-receipt-list">
              {(successPayouts?.ids || []).map((pid, idx) => (
                <div key={pid} className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => openReceipt(pid)}
                    className="flex-1 justify-between"
                    data-testid={`payouts-success-receipt-${pid}`}
                  >
                    <span className="flex items-center gap-2">
                      <Receipt size={14} weight="duotone" />
                      Receipt #{idx + 1} · <span className="mono text-xs">{pid.slice(0, 8).toUpperCase()}</span>
                    </span>
                    <ArrowSquareOut size={14} />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => emailReceipt(pid)}
                    disabled={emailingIds.has(pid)}
                    className="shrink-0 bg-[#184A30] hover:bg-[#0F3320] text-white border-[#184A30]"
                    data-testid={`payouts-success-email-${pid}`}
                    title="Email this receipt to the agent"
                  >
                    <Envelope size={14} />
                    <span className="ml-1 text-xs">{emailingIds.has(pid) ? "Sending…" : "Email"}</span>
                  </Button>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button onClick={() => setSuccessPayouts(null)} variant="outline" data-testid="payouts-success-close">Done</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={confirmOpen} onOpenChange={(v) => { if (!busy) setConfirmOpen(v); }}>
          <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-lg" data-testid="payouts-confirm-dialog">
            <DialogHeader>
              <DialogTitle className="serif text-3xl text-[#184A30] flex items-center gap-2">
                <Receipt size={26} weight="fill" className="text-emerald-600" /> Process payment
              </DialogTitle>
              <DialogDescription className="text-[#4A5C50]">
                You&apos;re about to process payment of <span className="font-semibold text-[#112318]">{formatUSD(selectedSummary.totalAmt)}</span> across <span className="font-semibold text-[#112318]">{selectedSummary.agents.length}</span> agent{selectedSummary.agents.length === 1 ? "" : "s"} ({selectedSummary.count} commission row{selectedSummary.count === 1 ? "" : "s"}). A formal payment receipt will be generated for each agent.
              </DialogDescription>
            </DialogHeader>

            {selectedSummary.missingBank > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 text-amber-900 text-xs p-3 flex gap-2" data-testid="payouts-confirm-missing-bank-warning">
                <Warning size={14} weight="fill" className="mt-0.5 shrink-0" />
                <div>
                  <strong>{selectedSummary.missingBank}</strong> selected row{selectedSummary.missingBank === 1 ? "" : "s"} belong{selectedSummary.missingBank === 1 ? "s" : ""} to agents missing bank info. You can still mark them paid for bookkeeping, but you&apos;ll need to collect bank info before the actual wire/ACH lands.
                </div>
              </div>
            )}

            <div className="bg-white rounded-xl border border-[#E5E0D8] p-3 text-xs space-y-1 max-h-[160px] overflow-y-auto" data-testid="payouts-confirm-agent-breakdown">
              {selectedSummary.agents.map(a => (
                <div key={a.agent_id} className="flex items-center justify-between">
                  <span>
                    <span className="font-semibold">{a.agent_full_name}</span>
                    <span className="mono text-[#4A5C50] ml-2">{a.agent_code}</span>
                    {!a.bank_info_complete && <span className="ml-1 text-amber-700">⚠</span>}
                  </span>
                  <span className="mono">{a.count} × · {formatUSD(a.amount)}</span>
                </div>
              ))}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="paid_ref" className="text-xs uppercase tracking-widest text-[#4A5C50]">Payment reference (optional)</Label>
              <Input
                id="paid_ref" type="text"
                value={paidRef}
                onChange={(e) => setPaidRef(e.target.value)}
                placeholder="e.g. ACH batch 2026-02-18 or Wire #45728"
                disabled={busy}
                data-testid="payouts-confirm-ref"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="paid_note" className="text-xs uppercase tracking-widest text-[#4A5C50]">Note (optional)</Label>
              <Input
                id="paid_note" type="text"
                value={paidNote}
                onChange={(e) => setPaidNote(e.target.value)}
                placeholder="Internal memo for the audit log"
                disabled={busy}
                data-testid="payouts-confirm-note"
              />
            </div>

            <label
              className="flex items-start gap-3 cursor-pointer rounded-lg border border-[#E5E0D8] bg-white px-3 py-3 hover:bg-stone-50 transition"
              data-testid="payouts-auto-email-toggle"
            >
              <input
                type="checkbox"
                checked={autoEmail}
                onChange={(e) => setAutoEmail(e.target.checked)}
                disabled={busy}
                className="mt-0.5 h-4 w-4 rounded border-[#4A5C50] accent-[#184A30]"
              />
              <span className="text-xs text-[#112318] leading-snug">
                <span className="font-semibold flex items-center gap-1.5">
                  <Envelope size={12} weight="duotone" />
                  Auto-email receipts to agents after processing
                </span>
                <span className="block mt-0.5 text-[11px] text-[#4A5C50]">
                  Each agent immediately receives their payment receipt via Resend. Preference is remembered for next time.
                </span>
              </span>
            </label>

            <DialogFooter>
              <Button variant="outline" disabled={busy} onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                onClick={confirmMarkPaid}
                disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="payouts-confirm-submit-btn"
              >
                {busy
                  ? (autoEmail ? "Processing & emailing…" : "Processing payment…")
                  : `Process payment${autoEmail ? " + email" : ""} · ${formatUSD(selectedSummary.totalAmt)}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* NACHA download success dialog */}
      <Dialog open={nachaResultOpen} onOpenChange={setNachaResultOpen}>
        <DialogContent className="bg-[#FBF9F6] border-[#E5E0D8] max-w-md" data-testid="payouts-nacha-result-dialog">
          <DialogHeader>
            <DialogTitle className="serif text-3xl text-[#184A30] flex items-center gap-2">
              <DownloadSimple size={26} weight="fill" className="text-emerald-600" /> NACHA file downloaded
            </DialogTitle>
            <DialogDescription className="text-[#4A5C50]">
              Upload <span className="font-mono text-[#112318]">{nachaResult?.filename}</span> to your bank&apos;s ACH portal. The bank will debit your originating account and credit each agent on the effective entry date.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-white rounded-xl border border-[#E5E0D8] p-3 text-sm space-y-2">
            <div className="flex justify-between"><span className="text-[#4A5C50]">Entries</span><span className="mono font-semibold">{nachaResult?.entryCount}</span></div>
            <div className="flex justify-between"><span className="text-[#4A5C50]">Total credit</span><span className="mono font-semibold">{formatUSD(nachaResult?.totalUsd || 0)}</span></div>
            {(nachaResult?.skipped || 0) > 0 && (
              <div className="flex justify-between text-amber-700">
                <span>Skipped (no/invalid US ACH bank)</span>
                <span className="mono">{nachaResult.skipped}</span>
              </div>
            )}
            <div className="flex justify-between text-[10px] mono text-[#4A5C50] pt-1 border-t border-[#E5E0D8]">
              <span>File ID</span><span>{nachaResult?.fileId?.slice(0, 8).toUpperCase()}</span>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setNachaResultOpen(false)} variant="outline" data-testid="payouts-nacha-result-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
